import { z } from "zod";

// Schemas for the first-class DataForSEO MCP tools (`seo_*`) registered by
// bedrock-api. Hard caps here mirror the clamps in the tool layer so cost can
// never scale past these bounds. Outputs are trimmed shapes mapped from
// DataForSEO responses, never raw payloads.

// Human-readable locale params shared by most tools. The tool layer maps these
// to DataForSEO's `location_name` / `language_name` body fields and applies
// United States / English defaults when omitted.
const locationNameSchema = z.string().min(1).optional();
const languageNameSchema = z.string().min(1).optional();

const nullableNumberSchema = z.number().nullable();
const nullableStringSchema = z.string().nullable();

// ---------------------------------------------------------------------------
// DataForSEO Labs
// ---------------------------------------------------------------------------

export const dataForSeoMcpKeywordsOverviewInputSchema = z
  .object({
    keywords: z.array(z.string().min(1)).min(1).max(700),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
  })
  .describe("DataForSeoMcpKeywordsOverviewInput");

export const dataForSeoMcpKeywordsOverviewOutputSchema = z
  .object({
    locationName: z.string(),
    languageName: z.string(),
    keywords: z.array(
      z.object({
        keyword: z.string(),
        searchVolume: nullableNumberSchema,
        cpc: nullableNumberSchema,
        competition: nullableNumberSchema,
        competitionLevel: nullableStringSchema,
        keywordDifficulty: nullableNumberSchema,
        searchIntent: nullableStringSchema,
      }),
    ),
  })
  .describe("DataForSeoMcpKeywordsOverviewOutput");

export const dataForSeoMcpKeywordSuggestionsInputSchema = z
  .object({
    keyword: z.string().min(1),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
    limit: z.number().int().min(1).max(1000).optional(),
  })
  .describe("DataForSeoMcpKeywordSuggestionsInput");

export const dataForSeoMcpKeywordSuggestionsOutputSchema = z
  .object({
    seedKeyword: z.string(),
    items: z.array(
      z.object({
        keyword: z.string(),
        searchVolume: nullableNumberSchema,
        cpc: nullableNumberSchema,
        competition: nullableNumberSchema,
        keywordDifficulty: nullableNumberSchema,
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpKeywordSuggestionsOutput");

export const dataForSeoMcpKeywordDifficultyInputSchema = z
  .object({
    keywords: z.array(z.string().min(1)).min(1).max(1000),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
  })
  .describe("DataForSeoMcpKeywordDifficultyInput");

export const dataForSeoMcpKeywordDifficultyOutputSchema = z
  .object({
    items: z.array(
      z.object({
        keyword: z.string(),
        keywordDifficulty: nullableNumberSchema,
      }),
    ),
  })
  .describe("DataForSeoMcpKeywordDifficultyOutput");

export const dataForSeoMcpDomainOverviewInputSchema = z
  .object({
    target: z.string().min(1),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
  })
  .describe("DataForSeoMcpDomainOverviewInput");

export const dataForSeoMcpDomainOverviewOutputSchema = z
  .object({
    target: z.string(),
    locationName: z.string(),
    languageName: z.string(),
    metrics: z.object({
      organicCount: nullableNumberSchema,
      organicEtv: nullableNumberSchema,
      organicPos1to3: nullableNumberSchema,
      paidCount: nullableNumberSchema,
      paidEtv: nullableNumberSchema,
    }),
  })
  .describe("DataForSeoMcpDomainOverviewOutput");

export const dataForSeoMcpDomainRankedKeywordsInputSchema = z
  .object({
    target: z.string().min(1),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
    limit: z.number().int().min(1).max(1000).optional(),
  })
  .describe("DataForSeoMcpDomainRankedKeywordsInput");

export const dataForSeoMcpDomainRankedKeywordsOutputSchema = z
  .object({
    target: z.string(),
    items: z.array(
      z.object({
        keyword: z.string(),
        rankAbsolute: nullableNumberSchema,
        url: nullableStringSchema,
        searchVolume: nullableNumberSchema,
        keywordDifficulty: nullableNumberSchema,
        etv: nullableNumberSchema,
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpDomainRankedKeywordsOutput");

export const dataForSeoMcpDomainCompetitorsInputSchema = z
  .object({
    target: z.string().min(1),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
    limit: z.number().int().min(1).max(100).optional(),
  })
  .describe("DataForSeoMcpDomainCompetitorsInput");

export const dataForSeoMcpDomainCompetitorsOutputSchema = z
  .object({
    target: z.string(),
    items: z.array(
      z.object({
        domain: z.string(),
        avgPosition: nullableNumberSchema,
        intersections: nullableNumberSchema,
        etv: nullableNumberSchema,
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpDomainCompetitorsOutput");

export const dataForSeoMcpDomainTrafficInputSchema = z
  .object({
    targets: z.array(z.string().min(1)).min(1).max(100),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
  })
  .describe("DataForSeoMcpDomainTrafficInput");

export const dataForSeoMcpDomainTrafficOutputSchema = z
  .object({
    items: z.array(
      z.object({
        target: z.string(),
        organicEtv: nullableNumberSchema,
        organicCount: nullableNumberSchema,
        paidEtv: nullableNumberSchema,
        paidCount: nullableNumberSchema,
      }),
    ),
  })
  .describe("DataForSeoMcpDomainTrafficOutput");

export const dataForSeoMcpDomainIntersectionInputSchema = z
  .object({
    target1: z.string().min(1),
    target2: z.string().min(1),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
    limit: z.number().int().min(1).max(100).optional(),
  })
  .describe("DataForSeoMcpDomainIntersectionInput");

const domainIntersectionSerpElementSchema = z.object({
  rankAbsolute: nullableNumberSchema,
  url: nullableStringSchema,
  etv: nullableNumberSchema,
});

export const dataForSeoMcpDomainIntersectionOutputSchema = z
  .object({
    target1: z.string(),
    target2: z.string(),
    items: z.array(
      z.object({
        keyword: z.string(),
        searchVolume: nullableNumberSchema,
        keywordDifficulty: nullableNumberSchema,
        firstDomain: domainIntersectionSerpElementSchema,
        secondDomain: domainIntersectionSerpElementSchema,
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpDomainIntersectionOutput");

// ---------------------------------------------------------------------------
// SERP
// ---------------------------------------------------------------------------

export const dataForSeoMcpSerpSearchInputSchema = z
  .object({
    keyword: z.string().min(1),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
    depth: z.number().int().min(1).max(100).optional(),
  })
  .describe("DataForSeoMcpSerpSearchInput");

export const dataForSeoMcpSerpSearchOutputSchema = z
  .object({
    keyword: z.string(),
    locationName: z.string(),
    languageName: z.string(),
    seResultsCount: nullableNumberSchema,
    items: z.array(
      z.object({
        rankAbsolute: nullableNumberSchema,
        title: nullableStringSchema,
        url: nullableStringSchema,
        domain: nullableStringSchema,
        description: nullableStringSchema,
      }),
    ),
  })
  .describe("DataForSeoMcpSerpSearchOutput");

// ---------------------------------------------------------------------------
// Keywords Data (Google Ads)
// ---------------------------------------------------------------------------

const monthlySearchSchema = z.object({
  year: nullableNumberSchema,
  month: nullableNumberSchema,
  searchVolume: nullableNumberSchema,
});

export const dataForSeoMcpKeywordsSearchVolumeInputSchema = z
  .object({
    keywords: z.array(z.string().min(1)).min(1).max(1000),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
  })
  .describe("DataForSeoMcpKeywordsSearchVolumeInput");

export const dataForSeoMcpKeywordsSearchVolumeOutputSchema = z
  .object({
    items: z.array(
      z.object({
        keyword: z.string(),
        searchVolume: nullableNumberSchema,
        cpc: nullableNumberSchema,
        competitionIndex: nullableNumberSchema,
        monthlySearches: z.array(monthlySearchSchema),
      }),
    ),
  })
  .describe("DataForSeoMcpKeywordsSearchVolumeOutput");

export const dataForSeoMcpKeywordsForSiteInputSchema = z
  .object({
    target: z.string().min(1),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
  })
  .describe("DataForSeoMcpKeywordsForSiteInput");

export const dataForSeoMcpKeywordsForSiteOutputSchema = z
  .object({
    target: z.string(),
    items: z.array(
      z.object({
        keyword: z.string(),
        searchVolume: nullableNumberSchema,
        cpc: nullableNumberSchema,
        competitionIndex: nullableNumberSchema,
      }),
    ),
  })
  .describe("DataForSeoMcpKeywordsForSiteOutput");

// ---------------------------------------------------------------------------
// Domain Analytics
// ---------------------------------------------------------------------------

export const dataForSeoMcpDomainTechnologiesInputSchema = z
  .object({
    target: z.string().min(1),
  })
  .describe("DataForSeoMcpDomainTechnologiesInput");

export const dataForSeoMcpDomainTechnologiesOutputSchema = z
  .object({
    domain: z.string(),
    title: nullableStringSchema,
    domainRank: nullableNumberSchema,
    countryIsoCode: nullableStringSchema,
    groups: z.array(
      z.object({
        group: z.string(),
        category: z.string(),
        technologies: z.array(z.string()),
      }),
    ),
  })
  .describe("DataForSeoMcpDomainTechnologiesOutput");

export const dataForSeoMcpDomainWhoisInputSchema = z
  .object({
    domain: z.string().min(1),
  })
  .describe("DataForSeoMcpDomainWhoisInput");

export const dataForSeoMcpDomainWhoisOutputSchema = z
  .object({
    domain: z.string(),
    registered: z.boolean().nullable(),
    registrar: nullableStringSchema,
    createdAt: nullableStringSchema,
    expiresAt: nullableStringSchema,
    updatedAt: nullableStringSchema,
    eppStatusCodes: z.array(z.string()),
    organicEtv: nullableNumberSchema,
    organicCount: nullableNumberSchema,
    backlinks: nullableNumberSchema,
    referringDomains: nullableNumberSchema,
  })
  .describe("DataForSeoMcpDomainWhoisOutput");

// ---------------------------------------------------------------------------
// Backlinks
// ---------------------------------------------------------------------------

export const dataForSeoMcpBacklinksSummaryInputSchema = z
  .object({
    target: z.string().min(1),
  })
  .describe("DataForSeoMcpBacklinksSummaryInput");

export const dataForSeoMcpBacklinksSummaryOutputSchema = z
  .object({
    target: z.string(),
    rank: nullableNumberSchema,
    backlinks: nullableNumberSchema,
    referringDomains: nullableNumberSchema,
    referringMainDomains: nullableNumberSchema,
    referringPages: nullableNumberSchema,
    brokenBacklinks: nullableNumberSchema,
    brokenPages: nullableNumberSchema,
  })
  .describe("DataForSeoMcpBacklinksSummaryOutput");

export const dataForSeoMcpBacklinksListInputSchema = z
  .object({
    target: z.string().min(1),
    limit: z.number().int().min(1).max(1000).optional(),
  })
  .describe("DataForSeoMcpBacklinksListInput");

export const dataForSeoMcpBacklinksListOutputSchema = z
  .object({
    target: z.string(),
    items: z.array(
      z.object({
        urlFrom: nullableStringSchema,
        domainFrom: nullableStringSchema,
        urlTo: nullableStringSchema,
        anchor: nullableStringSchema,
        dofollow: z.boolean().nullable(),
        rank: nullableNumberSchema,
        firstSeen: nullableStringSchema,
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpBacklinksListOutput");

export const dataForSeoMcpReferringDomainsInputSchema = z
  .object({
    target: z.string().min(1),
    limit: z.number().int().min(1).max(1000).optional(),
  })
  .describe("DataForSeoMcpReferringDomainsInput");

export const dataForSeoMcpReferringDomainsOutputSchema = z
  .object({
    target: z.string(),
    items: z.array(
      z.object({
        domain: z.string(),
        rank: nullableNumberSchema,
        backlinks: nullableNumberSchema,
        referringPages: nullableNumberSchema,
        firstSeen: nullableStringSchema,
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpReferringDomainsOutput");

export const dataForSeoMcpBacklinksBulkMetricsInputSchema = z
  .object({
    targets: z.array(z.string().min(1)).min(1).max(100),
  })
  .describe("DataForSeoMcpBacklinksBulkMetricsInput");

export const dataForSeoMcpBacklinksBulkMetricsOutputSchema = z
  .object({
    items: z.array(
      z.object({
        target: z.string(),
        backlinks: nullableNumberSchema,
      }),
    ),
  })
  .describe("DataForSeoMcpBacklinksBulkMetricsOutput");

// ---------------------------------------------------------------------------
// AI Optimization (LLM Mentions + AI keyword data)
// ---------------------------------------------------------------------------

export const dataForSeoMcpLlmPlatformSchema = z.enum(["google", "chat_gpt"]);

// Each target entity must contain exactly one of `keyword` or `domain`; the
// tool layer enforces this because a top-level refine would break `.shape`
// access required by MCP tool registration.
const llmMentionsTargetSchema = z.object({
  keyword: z.string().min(1).optional(),
  domain: z.string().min(1).optional(),
});

export const dataForSeoMcpLlmMentionsInputSchema = z
  .object({
    targets: z.array(llmMentionsTargetSchema).min(1).max(10),
    platform: dataForSeoMcpLlmPlatformSchema.optional(),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
    limit: z.number().int().min(1).max(100).optional(),
  })
  .describe("DataForSeoMcpLlmMentionsInput");

export const dataForSeoMcpLlmMentionsOutputSchema = z
  .object({
    totalCount: nullableNumberSchema,
    items: z.array(
      z.object({
        platform: nullableStringSchema,
        modelName: nullableStringSchema,
        question: nullableStringSchema,
        answerExcerpt: nullableStringSchema,
        aiSearchVolume: nullableNumberSchema,
        sources: z.array(
          z.object({
            title: nullableStringSchema,
            domain: nullableStringSchema,
            url: nullableStringSchema,
          }),
        ),
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpLlmMentionsOutput");

export const dataForSeoMcpLlmMentionsOverviewInputSchema = z
  .object({
    targets: z.array(llmMentionsTargetSchema).min(1).max(10),
    platform: dataForSeoMcpLlmPlatformSchema.optional(),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
  })
  .describe("DataForSeoMcpLlmMentionsOverviewInput");

const llmMentionsGroupSchema = z.object({
  key: z.string(),
  mentions: nullableNumberSchema,
  aiSearchVolume: nullableNumberSchema,
});

export const dataForSeoMcpLlmMentionsOverviewOutputSchema = z
  .object({
    total: z.object({
      mentions: nullableNumberSchema,
      aiSearchVolume: nullableNumberSchema,
    }),
    byPlatform: z.array(llmMentionsGroupSchema),
    bySourceDomain: z.array(llmMentionsGroupSchema),
  })
  .describe("DataForSeoMcpLlmMentionsOverviewOutput");

export const dataForSeoMcpAiKeywordVolumeInputSchema = z
  .object({
    keywords: z.array(z.string().min(1)).min(1).max(1000),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
  })
  .describe("DataForSeoMcpAiKeywordVolumeInput");

export const dataForSeoMcpAiKeywordVolumeOutputSchema = z
  .object({
    items: z.array(
      z.object({
        keyword: z.string(),
        aiSearchVolume: nullableNumberSchema,
        aiMonthlySearches: z.array(
          z.object({
            year: nullableNumberSchema,
            month: nullableNumberSchema,
            aiSearchVolume: nullableNumberSchema,
          }),
        ),
      }),
    ),
  })
  .describe("DataForSeoMcpAiKeywordVolumeOutput");

// ---------------------------------------------------------------------------
// OnPage
// ---------------------------------------------------------------------------

export const dataForSeoMcpPageAuditInputSchema = z
  .object({
    url: z.string().min(1),
    enableJavascript: z.boolean().optional(),
  })
  .describe("DataForSeoMcpPageAuditInput");

export const dataForSeoMcpPageAuditOutputSchema = z
  .object({
    url: z.string(),
    statusCode: nullableNumberSchema,
    onpageScore: nullableNumberSchema,
    title: nullableStringSchema,
    description: nullableStringSchema,
    contentWordCount: nullableNumberSchema,
    loadTimeMs: nullableNumberSchema,
    // Check names that returned true. DataForSEO mixes issue flags (e.g.
    // no_image_alt) with positive indicators (e.g. seo_friendly_url), so these
    // are "flagged", not strictly "failed".
    flaggedChecks: z.array(z.string()),
  })
  .describe("DataForSeoMcpPageAuditOutput");

export const dataForSeoMcpSiteAuditStartInputSchema = z
  .object({
    target: z.string().min(1),
    maxCrawlPages: z.number().int().min(1).max(100).optional(),
  })
  .describe("DataForSeoMcpSiteAuditStartInput");

export const dataForSeoMcpSiteAuditStartOutputSchema = z
  .object({
    taskId: z.string(),
    target: z.string(),
    maxCrawlPages: z.number(),
    nextAction: z.string(),
  })
  .describe("DataForSeoMcpSiteAuditStartOutput");

export const dataForSeoMcpSiteAuditResultsInputSchema = z
  .object({
    taskId: z.string().min(1),
  })
  .describe("DataForSeoMcpSiteAuditResultsInput");

export const dataForSeoMcpSiteAuditResultsOutputSchema = z
  .object({
    taskId: z.string(),
    pending: z.boolean(),
    crawlProgress: nullableStringSchema,
    pagesCrawled: nullableNumberSchema,
    pagesInQueue: nullableNumberSchema,
    onpageScore: nullableNumberSchema,
    // check name -> number of crawled pages failing that check
    failedChecks: z.array(
      z.object({
        check: z.string(),
        pagesFailing: z.number(),
      }),
    ),
  })
  .describe("DataForSeoMcpSiteAuditResultsOutput");

// ---------------------------------------------------------------------------
// Content Analysis
// ---------------------------------------------------------------------------

export const dataForSeoMcpContentCitationsInputSchema = z
  .object({
    keyword: z.string().min(1),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .describe("DataForSeoMcpContentCitationsInput");

export const dataForSeoMcpContentCitationsOutputSchema = z
  .object({
    keyword: z.string(),
    totalCount: nullableNumberSchema,
    items: z.array(
      z.object({
        url: nullableStringSchema,
        domain: nullableStringSchema,
        title: nullableStringSchema,
        snippet: nullableStringSchema,
        sentimentPositive: nullableNumberSchema,
        sentimentNegative: nullableNumberSchema,
        fetchTime: nullableStringSchema,
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpContentCitationsOutput");

export const dataForSeoMcpContentSentimentInputSchema = z
  .object({
    keyword: z.string().min(1),
  })
  .describe("DataForSeoMcpContentSentimentInput");

export const dataForSeoMcpContentSentimentOutputSchema = z
  .object({
    keyword: z.string(),
    totalCount: nullableNumberSchema,
    connotationTypes: z.object({
      positive: nullableNumberSchema,
      negative: nullableNumberSchema,
      neutral: nullableNumberSchema,
    }),
    sentimentConnotations: z.object({
      anger: nullableNumberSchema,
      happiness: nullableNumberSchema,
      love: nullableNumberSchema,
      sadness: nullableNumberSchema,
      share: nullableNumberSchema,
      fun: nullableNumberSchema,
    }),
    topDomains: z.array(
      z.object({
        domain: z.string(),
        count: nullableNumberSchema,
      }),
    ),
  })
  .describe("DataForSeoMcpContentSentimentOutput");

// ---------------------------------------------------------------------------
// Business Data
// ---------------------------------------------------------------------------

export const dataForSeoMcpBusinessListingsInputSchema = z
  .object({
    // Business name to search for (matches DataForSEO's `title` field).
    name: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    categories: z.array(z.string().min(1)).max(10).optional(),
    // "latitude,longitude,radiusKm" per DataForSEO's location_coordinate format.
    locationCoordinate: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .describe("DataForSeoMcpBusinessListingsInput");

export const dataForSeoMcpBusinessListingsOutputSchema = z
  .object({
    items: z.array(
      z.object({
        name: nullableStringSchema,
        category: nullableStringSchema,
        address: nullableStringSchema,
        phone: nullableStringSchema,
        url: nullableStringSchema,
        domain: nullableStringSchema,
        rating: nullableNumberSchema,
        reviewsCount: nullableNumberSchema,
        isClaimed: z.boolean().nullable(),
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpBusinessListingsOutput");

export const dataForSeoMcpReviewsPlatformSchema = z.enum([
  "google",
  "trustpilot",
  "tripadvisor",
]);

export const dataForSeoMcpReviewsStartInputSchema = z
  .object({
    platform: dataForSeoMcpReviewsPlatformSchema,
    // Business identifier: business name for Google/Tripadvisor, the reviewed
    // domain for Trustpilot (e.g. "www.thepearlsource.com").
    target: z.string().min(1),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
    depth: z.number().int().min(1).max(100).optional(),
  })
  .describe("DataForSeoMcpReviewsStartInput");

export const dataForSeoMcpReviewsStartOutputSchema = z
  .object({
    taskId: z.string(),
    platform: dataForSeoMcpReviewsPlatformSchema,
    target: z.string(),
    nextAction: z.string(),
  })
  .describe("DataForSeoMcpReviewsStartOutput");

export const dataForSeoMcpReviewsResultInputSchema = z
  .object({
    platform: dataForSeoMcpReviewsPlatformSchema,
    taskId: z.string().min(1),
  })
  .describe("DataForSeoMcpReviewsResultInput");

export const dataForSeoMcpReviewsResultOutputSchema = z
  .object({
    taskId: z.string(),
    pending: z.boolean(),
    title: nullableStringSchema,
    reviewsCount: nullableNumberSchema,
    items: z.array(
      z.object({
        rating: nullableNumberSchema,
        reviewText: nullableStringSchema,
        authorName: nullableStringSchema,
        timestamp: nullableStringSchema,
        url: nullableStringSchema,
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpReviewsResultOutput");

// ---------------------------------------------------------------------------
// Merchant (Google Shopping)
// ---------------------------------------------------------------------------

export const dataForSeoMcpMerchantProductsStartInputSchema = z
  .object({
    keyword: z.string().min(1),
    locationName: locationNameSchema,
    languageName: languageNameSchema,
    depth: z.number().int().min(1).max(100).optional(),
  })
  .describe("DataForSeoMcpMerchantProductsStartInput");

export const dataForSeoMcpMerchantProductsStartOutputSchema = z
  .object({
    taskId: z.string(),
    keyword: z.string(),
    nextAction: z.string(),
  })
  .describe("DataForSeoMcpMerchantProductsStartOutput");

export const dataForSeoMcpMerchantProductsResultInputSchema = z
  .object({
    taskId: z.string().min(1),
  })
  .describe("DataForSeoMcpMerchantProductsResultInput");

export const dataForSeoMcpMerchantProductsResultOutputSchema = z
  .object({
    taskId: z.string(),
    pending: z.boolean(),
    items: z.array(
      z.object({
        title: nullableStringSchema,
        seller: nullableStringSchema,
        price: nullableNumberSchema,
        currency: nullableStringSchema,
        rating: nullableNumberSchema,
        ratingVotesCount: nullableNumberSchema,
        url: nullableStringSchema,
      }),
    ),
    itemsCount: z.number(),
  })
  .describe("DataForSeoMcpMerchantProductsResultOutput");

export const knownDataForSeoMcpToolMetadataRegistrySchema = z
  .object({
    seo_keywords_overview: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpKeywordsOverviewInputSchema,
      output: dataForSeoMcpKeywordsOverviewOutputSchema,
    }),
    seo_keyword_suggestions: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpKeywordSuggestionsInputSchema,
      output: dataForSeoMcpKeywordSuggestionsOutputSchema,
    }),
    seo_keyword_difficulty: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpKeywordDifficultyInputSchema,
      output: dataForSeoMcpKeywordDifficultyOutputSchema,
    }),
    seo_domain_overview: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpDomainOverviewInputSchema,
      output: dataForSeoMcpDomainOverviewOutputSchema,
    }),
    seo_domain_ranked_keywords: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpDomainRankedKeywordsInputSchema,
      output: dataForSeoMcpDomainRankedKeywordsOutputSchema,
    }),
    seo_domain_competitors: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpDomainCompetitorsInputSchema,
      output: dataForSeoMcpDomainCompetitorsOutputSchema,
    }),
    seo_domain_traffic: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpDomainTrafficInputSchema,
      output: dataForSeoMcpDomainTrafficOutputSchema,
    }),
    seo_domain_intersection: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpDomainIntersectionInputSchema,
      output: dataForSeoMcpDomainIntersectionOutputSchema,
    }),
    seo_serp_search: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpSerpSearchInputSchema,
      output: dataForSeoMcpSerpSearchOutputSchema,
    }),
    seo_keywords_search_volume: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpKeywordsSearchVolumeInputSchema,
      output: dataForSeoMcpKeywordsSearchVolumeOutputSchema,
    }),
    seo_keywords_for_site: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpKeywordsForSiteInputSchema,
      output: dataForSeoMcpKeywordsForSiteOutputSchema,
    }),
    seo_domain_technologies: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpDomainTechnologiesInputSchema,
      output: dataForSeoMcpDomainTechnologiesOutputSchema,
    }),
    seo_domain_whois: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpDomainWhoisInputSchema,
      output: dataForSeoMcpDomainWhoisOutputSchema,
    }),
    seo_backlinks_summary: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpBacklinksSummaryInputSchema,
      output: dataForSeoMcpBacklinksSummaryOutputSchema,
    }),
    seo_backlinks_list: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpBacklinksListInputSchema,
      output: dataForSeoMcpBacklinksListOutputSchema,
    }),
    seo_referring_domains: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpReferringDomainsInputSchema,
      output: dataForSeoMcpReferringDomainsOutputSchema,
    }),
    seo_backlinks_bulk_metrics: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpBacklinksBulkMetricsInputSchema,
      output: dataForSeoMcpBacklinksBulkMetricsOutputSchema,
    }),
    seo_llm_mentions: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpLlmMentionsInputSchema,
      output: dataForSeoMcpLlmMentionsOutputSchema,
    }),
    seo_llm_mentions_overview: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpLlmMentionsOverviewInputSchema,
      output: dataForSeoMcpLlmMentionsOverviewOutputSchema,
    }),
    seo_ai_keyword_volume: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpAiKeywordVolumeInputSchema,
      output: dataForSeoMcpAiKeywordVolumeOutputSchema,
    }),
    seo_page_audit: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpPageAuditInputSchema,
      output: dataForSeoMcpPageAuditOutputSchema,
    }),
    seo_site_audit_start: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpSiteAuditStartInputSchema,
      output: dataForSeoMcpSiteAuditStartOutputSchema,
    }),
    seo_site_audit_results: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpSiteAuditResultsInputSchema,
      output: dataForSeoMcpSiteAuditResultsOutputSchema,
    }),
    seo_content_citations: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpContentCitationsInputSchema,
      output: dataForSeoMcpContentCitationsOutputSchema,
    }),
    seo_content_sentiment: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpContentSentimentInputSchema,
      output: dataForSeoMcpContentSentimentOutputSchema,
    }),
    seo_business_listings: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpBusinessListingsInputSchema,
      output: dataForSeoMcpBusinessListingsOutputSchema,
    }),
    seo_reviews_start: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpReviewsStartInputSchema,
      output: dataForSeoMcpReviewsStartOutputSchema,
    }),
    seo_reviews_result: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpReviewsResultInputSchema,
      output: dataForSeoMcpReviewsResultOutputSchema,
    }),
    seo_merchant_products_start: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpMerchantProductsStartInputSchema,
      output: dataForSeoMcpMerchantProductsStartOutputSchema,
    }),
    seo_merchant_products_result: z.object({
      iconKey: z.literal("bedrock"),
      input: dataForSeoMcpMerchantProductsResultInputSchema,
      output: dataForSeoMcpMerchantProductsResultOutputSchema,
    }),
  })
  .describe("KnownDataForSeoMcpToolMetadataRegistry");
